import { DEFAULT_FORM_ERROR_RESPONSE } from './constants/constants-response';
import { API_BASE } from './constants/constants-urls';
import {
  SUBDOMAIN_PIANO_LOGIN,
  ARCHIVE_URL,
} from './constants/constants-urls';
import { Cookies } from 'react-cookie';

/**
 * General utilities class for methods that don't belong elsewhere.
 */
export default class Utilities {

  static baseUrl = API_BASE[process.env.REACT_APP_ENVIRONMENT];

  /**
   * The suppressSubscribeLinks checks to see if the view
   * being used is with the App in the forgot-password view.
  */
  static suppressSubscribeLinks = window.location.search.includes('app=true') &&
                                  window.location.pathname.includes('forgot-password');

  /**
   * Higher-level wrapper for fetch() with JSON deserialization, error handling, etc.
   * @param {String} method - The HTTP method to use, either POST, GET, PATCH, or DELETE.
   * @param {String} endpoint - The URL/path of the API endpoint to use.
   * @param {[Object]} data - (Optional.) Used with POST/PATCH requests.
   * @returns {Promise} The fetch-based promise that will resolve with the results.
   */
  static fetchData(method = 'GET', endpoint = '', data, isMultiPart = false, suppliedHeaders) {
    let body = (method === 'POST' || method === 'PATCH') ? data : undefined; // Only POST/PATCH can have a body.

    let headers = suppliedHeaders;
    if (!headers) {
      headers = {
        Accept: 'application/json, text/javascript, */*',
        'Content-Type': 'application/json',
      };
    }

    if (isMultiPart) {
      body = data;
      delete headers['Content-Type'];
    }

    const options = {
      method,
      body,
      credentials: 'include',
      headers: new Headers(headers),
    };

    if (method === 'GET') {
      delete options.body;
    }

    return fetch(`${this.baseUrl}${endpoint}`, options)
      .then((response) => {
        if (!response.ok) {
          throw response;
        }
        return response.json();
      }).catch(error => ({ error }));
  }

  /**
   * Saves the settings for a particular users.
   * @param {{email: string, newPassword: string}} formData - Params needed to change the user's password.
   * @param {string} pwdToken
   * @returns {Promise} The fetch-based promise that will resolve with the results.
   */
  static postUserChangePassword(formData, pwdToken) {
    const data = { password: formData };

    return this.fetchData(
      'POST',
      `/user/changePassword?Site=bostonglobe&pwdToken=${encodeURIComponent(pwdToken)}`,
      JSON.stringify(data),
    );
  }

  /**
   * Posts to the login endpoint to verify the user.
   * @param {String} user - The user's username.
   * @param {String} password - The user's password.
   * @returns {Promise} The fetch-based promise that will resolve with the results.
   */
  static postLogin(user, password) {
    const formData = `user=${user}&password=${password}&setcookies=true&Site=bostonglobe`;
    const headers = {
      Accept: 'application/json, application/xml, text/plain, text/html, *.*',
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
    };
    return this.fetchData('POST', '/auth/login', formData, false, headers);
  }

  /**
   * If successful, retrieves settings for a particular user.
   * @param {string} token - pwdToken, is the key to look up the user.
   * @returns {Promise} The fetch-based promise that will resolve with the results.
   */
  static getUserByPwdToken(token) {
    return this.fetchData(
      'GET',
      `/user/getUserByPwdToken?pwdToken=${encodeURIComponent(token)}`,
      false,
      { 'Content-Type': 'application/json' },
    );
  }

  /**
   * If successful, retrieves settings for a particular user.
   * @param {string} token - pathAuthJWT token, is the key to look up the user.
   * @returns {Promise} The fetch-based promise that will resolve with the results.
   */
  static getUserByToken(token) {
    return this.fetchData(
      'GET',
      '/auth/getuserbytoken?Site=bostonglobe',
      false,
      {},
      {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    );
  }

  /**
     * Posts to the login endpoint to verify the user.
     * @param {String} user - The user's username.
     * @returns {Promise} The fetch-based promise that will resolve with the results.
     */
  static postForgotPassword(userEmail) {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    return this.fetchData('POST', '/user/forgotpassword?Site=bostonglobe', userEmail, false, headers);
  }

  /**
     * Sets invalid state of input.
     *
     * @param {Event.invalid} event
     */
  static onInvalidHandle({ target = {} }, { error = null }, customResponse = DEFAULT_FORM_ERROR_RESPONSE) {
    let Response = null;
    const { value = '', name = '' } = target;

    if (!name) {
      return error;
    }

    if (!value) {
      Response = customResponse;
    }

    return Object.assign(
      {},
      error,
      { [name]: true },
      { response: Response },
    );
  }

  /**
     * Sets error state of input on blur.
     *
     * @param {Event.blur|{}} target Event object.
     * @param {{*}} error Error state.
     *
     * @return {*|null} Current error state.
     */
  static onBlurHandle({ target = {} }, componentState) {
    const { name = '', value = '' } = target;
    let { error = null } = componentState;
    let inputsNotEmpty = true;
    const isPasswordValidLength = value.length >= 6 && value.length <= 20;

    if (!name || !error) {
      return error;
    }

    if (name === 'email') {
      // Use the same regex supplied by the browsers to determine if the email is valid.
      // @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email
      // eslint-disable-next-line
        const browserEmailRegX = new RegExp(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/);

      if (browserEmailRegX.test(value)) {
        error = Object.assign({}, error, { email: false });
      }
    } else if (name === 'password') { 
      if (isPasswordValidLength) {
        error = Object.assign({}, error, { [name]: false });
      } 
    } else if (name === 'confirmPassword' && value !== '' ) {

      const newPassword = componentState.password;
      if (newPassword === value && isPasswordValidLength) {
        error = Object.assign({}, error, { [name]: false });
      }  
    } else if (value) {
      error = Object.assign({}, error, { [name]: false });
    }

    Object.keys(error).forEach((key) => {
      if (key !== 'response' && inputsNotEmpty) {
        // Since we only show an error message when the the fields are empty.
        // Get the current values of the form inputs and check that they are not empty.
        const inputValue = componentState[key] || '';
        inputsNotEmpty = inputValue.length > 0;
      }
    });

    // checks to see if user is on the ResetPasswordForm
    const isResetPassword =  'confirmPassword' in componentState;

    if (isResetPassword) {
      const sameVals = name === 'password' ? 
                       value === componentState.confirmPassword : value === componentState.password;
      if (isPasswordValidLength && sameVals && inputsNotEmpty) {
        // Removes the error response
        error = Object.assign({}, error, { confirmPassword: false, response: '' });
      }
    } else {
      error = inputsNotEmpty ? Object.assign({}, error, { response: '' }) : error;
    }
    return error;
  }

  /**
   * Gets domain string for cookie
   * @method getCookieDomain
   * @return {String} Domain to be passed to cookie. If local
   *                  use localhost for testing.
   */
  static getCookieDomain() {
    const { hostname } = window.location;
    const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';

    return isLocal ? 'localhost' : '.bostonglobe.com';
  }

  static async redirectIfLoggedIn(isArchiveLogin, resetPasswordEvent = '') {
    const cookie = new Cookies();
    const pathUrlCookie = cookie.get('pathUrl');
    const pathUrl = pathUrlCookie ? decodeURIComponent(pathUrlCookie) : '';
    const environment = process.env.REACT_APP_ENVIRONMENT;
    
    // Checking for Piano login cookie
    if (cookie.get('__utp')) {
      if (isArchiveLogin === true) {
        const { message, tpaToken } = await this.getTPAToken();
        console.log('getTPAToken message:', message);
        console.log('tpaToken value:', tpaToken);
        if (message === 'Success') {
          console.log('SUCCESS TPA response');
          window.location = `${ARCHIVE_URL}${tpaToken}`;
        } else {
          alert('The archive failed to open. Please try refreshing the page.');
        }
      } else {
        console.log('REDIRECTING after login');
        window.location = `https://${SUBDOMAIN_PIANO_LOGIN[environment]}.bostonglobe.com${pathUrl}${resetPasswordEvent}`;
     }
   }
  }

  /**
   * Clear all user cookies
   * 
   */
  static clearCookies() {
    const cookie = new Cookies();
    const cookieDomain = Utilities.getCookieDomain();
    const cookieOptions = {
      path: '/',
      domain: cookieDomain,
    };
  
    // Remove other site-related cookies
    cookie.remove('AT', cookieOptions);
    cookie.remove('PSyncHint', cookieOptions);
    cookie.remove('TData', cookieOptions);  
  }

  /**
   * Get TPA token to use when redirecting to archive.
   * 
   */
  static getTPAToken() {
    return this.fetchData('GET', '/auth/tpa-token', false);
  }

  /**
   * Gets oneTrust active groups after customer consent choice
   * @method getOneTrustActiveGroup
   * @return {boolean} if categorized script should be loaded or not.
   */
  static getOneTrustActiveGroup(category) {
    const activeGroups = localStorage.getItem('OnetrustActiveGroups');
    // oneTrust scripts load slower than page analytics, before oneTrust loads, the activeGroups 
    // content could be null, undefined, or ,, we want to run all third party scripts before customer make choice not to.
    if (
      activeGroups === null
      || activeGroups === undefined
      || activeGroups === ',,'
      || activeGroups.indexOf(category) !== -1
    ) {
      return true;
    }
    return false;
  }

  /**
   * The returnCanonicalUrl get the canonical url string removing the query string if present.
   * @returns string: the window.location.href values minus the query string.
   */
  static returnCanonicalUrl = () => (
    window.location.href.includes('?') ? 
    window.location.href.split('?')[0] : window.location.href
  );
}
